import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useTranslation } from "react-i18next";
import { BarChart3, Users, Eye, MousePointerClick } from "lucide-react";

const EVENT_LABEL = {
  view_home: "Visite Home",
  view_apartment: "Visite appartamento",
  view_category: "Visite categoria",
  view_poi: "Visite POI",
  click_phone: "Click telefono",
  click_maps: "Click mappa",
  click_website: "Click sito web",
  click_email: "Click email",
  click_unlock_attempt: "Tentativi sblocco",
  click_language_switch: "Cambio lingua",
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-ink-200/40 p-5 shadow-soft">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-ink-300">{label}</div>
        <Icon strokeWidth={1.4} className="w-4 h-4 text-corten-400" />
      </div>
      <div className="mt-2 font-editorial italic text-4xl text-ink-900">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/analytics/summary?days=${range}`).then(({ data }) => setData(data));
  }, [range]);

  const maxCount = data?.by_event?.[0]?.count || 1;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="font-editorial italic text-4xl text-ink-900">{t("admin.dashboard")}</h1>
          <p className="text-xs text-ink-500 mt-1">Statistiche anonime della piattaforma</p>
        </div>
        <div className="flex gap-1 bg-cream-100 rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`analytics-range-${d}`}
              onClick={() => setRange(d)}
              className={`px-3 py-1 text-xs font-semibold rounded-full transition ${range === d ? "bg-corten-400 text-white" : "text-ink-500"}`}
            >
              {d}g
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard icon={Eye} label="Eventi totali" value={data?.total_events ?? "—"} />
        <StatCard icon={Users} label="Sessioni uniche" value={data?.unique_sessions ?? "—"} />
        <StatCard icon={MousePointerClick} label="Click contatti" value={
          (data?.by_event || [])
            .filter((x) => ["click_phone", "click_maps", "click_website", "click_email"].includes(x.event))
            .reduce((s, x) => s + x.count, 0)
        } />
      </div>

      <section className="mt-8 rounded-2xl bg-white border border-ink-200/40 p-5 shadow-soft">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 strokeWidth={1.4} className="w-4 h-4 text-corten-400" />
          <h2 className="font-editorial italic text-2xl">Eventi per tipo</h2>
        </div>
        <div className="flex flex-col gap-2">
          {(data?.by_event || []).length === 0 && (
            <p className="text-sm text-ink-300">Nessun evento ancora registrato.</p>
          )}
          {(data?.by_event || []).map((row) => (
            <div key={row.event}>
              <div className="flex justify-between text-xs text-ink-700 mb-1">
                <span>{EVENT_LABEL[row.event] || row.event}</span>
                <span className="tabular-nums font-semibold">{row.count}</span>
              </div>
              <div className="h-2 rounded-full bg-cream-100 overflow-hidden">
                <div
                  className="h-full bg-corten-400 rounded-full transition-all"
                  style={{ width: `${(row.count / maxCount) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6 rounded-2xl bg-white border border-ink-200/40 p-5 shadow-soft">
        <h2 className="font-editorial italic text-2xl mb-4">Contenuti più visti</h2>
        <div className="flex flex-col gap-1.5">
          {(data?.top_targets || []).slice(0, 15).map((row, i) => (
            <div key={i} className="flex items-center justify-between text-xs text-ink-700 border-b border-ink-200/30 py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-ink-300 tabular-nums w-6">{i + 1}.</span>
                <span className="text-ink-500">{EVENT_LABEL[row.event]}</span>
                <span className="font-mono text-[11px] text-ink-900 truncate">{row.target_id}</span>
              </div>
              <span className="tabular-nums font-semibold">{row.count}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
